import React, { Component, Fragment } from 'react';
import FormCheckbox from '../../../_shared/forms/FormCheckbox';
import FormInputNumber from '../../../_shared/forms/FormInputNumber';
import axios from 'axios';
import { promptPriceChangeReason } from '../../../../utils/Helpers';

export default class PricingDetails extends Component {
    constructor(props) {
        super(props);
        this.state = {
            newCost: { reason: '', cost: '0.00', notes: '' },
            additionalCosts: props.additionalCosts || [],
            showAdditionalCostModal: false,
            showDeleteModal: false,
            costToDelete: null,
            errors: {},
        };
    }

    reasonLabels = {
        heavy_load: 'Heavy Load',
        contaminated: 'Contaminated or Hazardous Waste',
        out_of_area: 'Out of Area',
        wasted_journey: 'Wasted Journey Fee',
        other: 'Other',
    };

    formatGBP = (pence) => (Number(pence || 0) / 100).toFixed(2);
    getReasonLabel = (cost) => {
        const key = cost?.reason || cost?.label;
        return this.reasonLabels[key] || key || 'Unknown';
    };
    getPrice = (custom, total, quoted) => {
        return quoted === 'yes' ? Number(custom) * 100 : Number(total);
    };

    getProviderPrice = (custom, total, quoted) => {
        const basePence = quoted === 'yes' ? Number(custom) * 100 : Number(total); 
        const additionalPence = this.state.additionalCosts.reduce((sum, c) => sum + Number(c.cost), 0); 
        return basePence + additionalPence; 
    };

    calculateMargin = (totalPence, providerPence) =>
        totalPence === 0 ? 'N/A' : (((totalPence - providerPence) / totalPence) * 100).toFixed(2) + '%';

    // Modals
    openAdditionalCostModal = () => this.setState({ showAdditionalCostModal: true });
    closeAdditionalCostModal = () =>
        this.setState({ showAdditionalCostModal: false, newCost: { reason: '', cost: '', notes: '' }, errors: {} });

    openDeleteModal = (cost) => this.setState({ showDeleteModal: true, costToDelete: cost });
    closeDeleteModal = () => this.setState({ showDeleteModal: false, costToDelete: null });

    handleNewCostChange = (e) => {
        const { name, value } = e.target;
        this.setState((prev) => ({ newCost: { ...prev.newCost, [name]: value } }));
    };

    handleRemoveCost = async (e, priceChangeReason) => {
        if (e) e.preventDefault();
        const { costToDelete } = this.state;
        if (!costToDelete) return;

        try {
            await axios.delete(`/jobs/${this.props.jobId}/additional-costs/${costToDelete.id}`, {
                data: priceChangeReason ? { price_change_reason: priceChangeReason } : {},
            });
            const updated = this.state.additionalCosts.filter((c) => c.id !== costToDelete.id);
            this.setState({ additionalCosts: updated, showDeleteModal: false, costToDelete: null });
            if (this.props.onAdditionalCostsChange) this.props.onAdditionalCostsChange(updated);
        } catch (error) {
            if (error.response?.status === 422 && error.response.data.requires_price_change_reason) {
                const reason = await promptPriceChangeReason();
                if (reason) this.handleRemoveCost(null, reason);
                return;
            }

            if (error.response?.status === 403 && error.response.data.message) {
                window.swal.fire({ icon: 'error', title: 'Unable to remove cost', text: error.response.data.message });
                return;
            }

            alert('Failed to remove cost. Please try again.');
            console.error(error);
        }
    };

    renderExtrasTable = () => {
        const allExtras = [...(this.props.extras || []), ...(this.props.sorCodes || []), ...(this.props.skips || [])];
        const jobTypeQuantity = this.props.quantity || 1;
        const hasJobType = this.props.jobType && this.props.jobTypePrice !== undefined && this.props.jobTypePrice !== null;
        if (!hasJobType && !allExtras.length) return null;
        return (
            <div className="table-responsive transform-text">
                <p>Job type &amp; extras</p>
                <table className="table table-bordered w-100 text-uppercase">
                    <thead className="thead-light">
                        <tr>
                            <th>Name</th>
                            <th>Quantity</th>
                            <th>Item Price (£)</th>
                            <th>Total Price (£)</th>
                            <th>Provider Quantity</th>
                            <th>Provider Price (£)</th>
                            <th>Total Provider Price (£)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {hasJobType && (
                            <tr>
                                <td>{this.props.jobType.name}</td>
                                <td>{jobTypeQuantity}</td>
                                <td>£{this.formatGBP(this.props.jobTypePrice)}</td>
                                <td>£{this.formatGBP(this.props.jobTypePrice * jobTypeQuantity)}</td>
                                <td>-</td>
                                <td>-</td>
                                <td>-</td>
                            </tr>
                        )}
                        {allExtras.map((extra, i) => (
                            <tr key={i}>
                                <td>{extra.label}</td>
                                <td>{extra.quantity}</td>
                                <td>£{this.formatGBP(extra.price)}</td>
                                <td>£{this.formatGBP(extra.price * extra.quantity)}</td>
                                <td>{extra.provider_quantity}</td>
                                <td>£{this.formatGBP(extra.providerPrice)}</td>
                                <td>£{this.formatGBP(extra.providerPrice * extra.provider_quantity)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };
    handleAddCost = async (e, priceChangeReason) => {
        if (e) e.preventDefault();
        const { newCost } = this.state;

        const payload = {
            reason: newCost.reason,
            notes: newCost.notes || null,
            cost: Math.round(parseFloat(newCost.cost || 0) * 100),
        };

        if (priceChangeReason) {
            payload.price_change_reason = priceChangeReason;
        }

        try {
            const response = await axios.post(`/jobs/${this.props.jobId}/additional-costs`, payload);

            const addedCost = {
                ...response.data.data,
                label: response.data.data.reason,
            };

            const updated = [...this.state.additionalCosts, addedCost];
            this.setState({ additionalCosts: updated, errors: {} });

            if (this.props.onAdditionalCostsChange) {
                this.props.onAdditionalCostsChange(updated);
            }

            this.closeAdditionalCostModal();
        } catch (error) {
            if (error.response?.status === 422 && error.response.data.requires_price_change_reason) {
                const reason = await promptPriceChangeReason();
                if (reason) this.handleAddCost(null, reason);
                return;
            }

            if (error.response?.status === 403 && error.response.data.message) {
                window.swal.fire({ icon: 'error', title: 'Unable to save', text: error.response.data.message });
                return;
            }

            if (error.response?.status === 422) {
                this.setState({ errors: error.response.data.errors || {} });
            } else {
                alert('Unexpected error adding cost.');
            }
        }
    };

    renderAdditionalCostsTable = () => {
        if (!this.state.additionalCosts.length) return null;

        return (
            <div className="table-responsive transform-text">
                <p>Additional Costs</p>
                <table className="table table-bordered w-100 text-uppercase">
                    <thead className="thead-light">
                        <tr>
                            <th>Reason</th>
                            <th>Cost (£)</th>
                            <th>Notes</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.additionalCosts.map((c, i) => {
                            return (
                                <tr key={i}>
                                    <td>{this.getReasonLabel(c)}</td>

                                    <td>£{this.formatGBP(c.cost)}</td>
                                    <td>{c.notes}</td>
                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm"
                                            onClick={() => this.openDeleteModal(c)}
                                        >
                                            <i className="fas fa-trash"></i> Remove
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    renderModal = ({ show, title, children, onClose, onConfirm, confirmText = 'Save', type = 'primary' }) => {
        if (!show) return null;
        return (
            <div
                className="modal fade show d-block"
                tabIndex="-1"
                role="dialog"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            >
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{title}</h5>
                            <button type="button" className="close" onClick={onClose}>
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">{children}</div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>
                                Cancel
                            </button>
                            {onConfirm && (
                                <button type="button" className={`btn btn-${type}`} onClick={onConfirm}>
                                    {confirmText}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const { newCost, errors, showAdditionalCostModal, showDeleteModal, costToDelete } = this.state;
        const totalPrice = this.getPrice(this.props.custom_price, this.props.totalPrice, this.props.price_quoted);
        const providerTotalPrice = this.getProviderPrice(
            this.props.provider_custom_price,
            this.props.providerTotalPrice,
            this.props.provider_price_quoted
        );
        return (
            <Fragment>
                {this.renderModal({
                    show: showAdditionalCostModal,
                    title: 'Add Additional Cost',
                    onClose: this.closeAdditionalCostModal,
                    onConfirm: this.handleAddCost,
                    children: (
                        <>
                            <div className="form-group">
                                <label>Reason</label>
                                <select
                                    name="reason"
                                    className="form-control"
                                    value={newCost.reason}
                                    onChange={this.handleNewCostChange}
                                >
                                    <option value="">Select cost type</option>
                                    <option value="heavy_load">Heavy Load</option>
                                    <option value="contaminated">Contaminated or Hazardous Waste</option>
                                    <option value="out_of_area">Out of Area</option>
                                    <option value="wasted_journey">Wasted Journey</option>

                                    <option value="other">Other</option>
                                </select>
                                {errors.reason && <small className="text-danger">{errors.reason[0]}</small>}
                            </div>

                            <div className="form-group">
                                <label>Amount (£)</label>
                                <input
                                    type="number"
                                    name="cost"
                                    step="0.01"
                                    className="form-control"
                                    value={newCost.cost}
                                    placeholder="0.00"
                                    onChange={this.handleNewCostChange}
                                />
                                {errors.cost && <small className="text-danger">{errors.cost[0]}</small>}
                            </div>

                            <div className="form-group">
                                <label>Notes {newCost.reason === 'other' && '(required)'}</label>
                                <textarea
                                    name="notes"
                                    className="form-control"
                                    value={newCost.notes}
                                    onChange={this.handleNewCostChange}
                                />
                                {errors.notes && <small className="text-danger">{errors.notes[0]}</small>}
                            </div>
                        </>
                    ),
                })}

                {this.renderModal({
                    show: showDeleteModal,
                    title: 'Remove Additional Cost',
                    onClose: this.closeDeleteModal,
                    onConfirm: this.handleRemoveCost,
                    confirmText: 'Remove',
                    type: 'danger',
                    children: costToDelete && (
                        <p>
                            Are you sure you want to remove "{this.getReasonLabel(costToDelete)}" (£
                            {this.formatGBP(costToDelete.cost)})?
                        </p>
                    ),
                })}

                {/* Rest of the page: extras table, additional costs table, totals, etc. */}
                <div className="row pb-3">
                    <div className="col-12">
                        <h6 className="py-2 pl-2 cw-bg-grey text-white">Report details</h6>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">{this.renderExtrasTable()}</div>
                </div>
                {this.props?.jobType?.name !== 'Tyres' && (
                    <div className="row mb-3">
                        <div className="col-12">
                            <button type="button" className="btn btn-primary" onClick={this.openAdditionalCostModal}>
                                Add Additional Cost
                            </button>
                        </div>
                    </div>
                )}

                {this.renderAdditionalCostsTable()}
                <div className="row mt-2">
                    <div className="col-md-3">Total price: £{this.formatGBP(totalPrice)}</div>
                    <div className="col-md-3">Provider total price: £{this.formatGBP(providerTotalPrice)}</div>
                    <div className="col-md-3">Margin: {this.calculateMargin(totalPrice, providerTotalPrice)}</div>
                </div>
                {this.props.price_quoted === 'yes' && (
                    <div className="row">
                        <div className="col-md-4">
                            <FormInputNumber
                                type="number"
                                label="Custom price"
                                name="custom_price"
                                step="0.01"
                                min="0"
                                classes="form-control"
                                value={this.props.custom_price || 0}
                                handleChange={this.props.handleCustomPriceChange}
                            />
                        </div>
                        <div className="col-md-4">
                            <FormInputNumber
                                type="number"
                                label="Custom price with vat"
                                name="custom_price_with_vat"
                                step="0.01"
                                min="0"
                                classes="form-control"
                                value={(this.props.custom_price_with_vat || 0).toFixed(2)}
                                handleChange={this.props.handleCustomPriceChange}
                            />
                        </div>
                    </div>
                )}

                {this.props.provider_price_quoted === 'yes' && (
                    <div className="row">
                        <div className="col-md-4">
                            <FormInputNumber
                                type="number"
                                label="Provider custom price"
                                name="provider_custom_price"
                                step="0.01"
                                min="0"
                                classes="form-control"
                                value={this.props.provider_custom_price || 0}
                                handleChange={this.props.handleProviderCustomPriceChange}
                            />
                        </div>
                        <div className="col-md-4">
                            <FormInputNumber
                                type="number"
                                label="Provider custom price with vat"
                                name="provider_custom_price_with_vat"
                                step="0.01"
                                min="0"
                                classes="form-control"
                                value={(this.props.provider_custom_price_with_vat || 0).toFixed(2)}
                                handleChange={this.props.handleProviderCustomPriceChange}
                            />
                        </div>
                    </div>
                )}

                <div className="row">
                    <div className="col-12">
                        <FormCheckbox
                            label="Price quoted"
                            name="price_quoted"
                            checked={this.props.price_quoted === 'yes'}
                            handleChange={this.props.handleChange}
                        />
                        <FormCheckbox
                            label="Provider price quoted"
                            name="provider_price_quoted"
                            checked={this.props.provider_price_quoted === 'yes'}
                            handleChange={this.props.handleChange}
                        />
                    </div>
                </div>
            </Fragment>
        );
    }
}
